import { useState } from 'react';
import { Eye, X } from 'lucide-react';
import {
    clearImpersonateSession,
    getImpersonateSession,
    type ImpersonateSession,
} from '../lib/impersonate';

/**
 * Persistent banner across every pax route when staff is impersonating a
 * customer. Sits sticky at the top of the viewport. Single "Stop" button
 * clears the session and reloads the dashboard.
 *
 * Customer name + email come from the impersonate token's payload (signed
 * server-side at issue time by pax-impersonate-issue), so no client-side
 * DB query is needed — which is good because the anon client on pax.*
 * doesn't have customer-read RLS.
 */
export function ImpersonationBanner() {
    const [session] = useState<ImpersonateSession | null>(() => getImpersonateSession());

    if (!session) return null;

    const handleStop = () => {
        clearImpersonateSession();
        // Hard reload back to dashboard so all in-memory state resets and the
        // staff member returns to whatever they were doing before.
        window.location.replace('/');
    };

    const expiresIn = Math.max(0, Math.round((new Date(session.expiresAt).getTime() - Date.now()) / 60_000));
    const label = session.customerName || session.customerEmail || `Customer ${session.customerId.slice(0, 8)}`;

    return (
        <div className="sticky top-0 z-50 w-full bg-amber-500 dark:bg-amber-600 text-white shadow-lg border-b-2 border-amber-700">
            <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                    <Eye className="w-4 h-4 shrink-0" />
                    <div className="text-sm font-medium truncate">
                        <span className="opacity-90">Staff impersonation —</span>{' '}
                        <span className="font-semibold">Viewing as {label}</span>
                        {session.customerEmail && session.customerName && (
                            <span className="opacity-75 ml-2 hidden sm:inline">({session.customerEmail})</span>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                    <span className="text-xs opacity-90 hidden sm:inline">Expires in {expiresIn} min</span>
                    <button
                        type="button"
                        onClick={handleStop}
                        className="flex items-center gap-1 px-3 py-1 bg-white/20 hover:bg-white/30 rounded text-xs font-semibold transition-colors"
                    >
                        <X className="w-3 h-3" />
                        Stop
                    </button>
                </div>
            </div>
        </div>
    );
}
